<script setup lang="ts">
  import { ref } from 'vue'
  import { type DetectedBarcode, QrcodeCapture, QrcodeStream } from 'vue-qrcode-reader'
  import { useRouter } from 'vue-router'
  import { useToast } from '@/functions/toast'

  const router = useRouter()

  const torchActive = ref(false)
  const torchNotSupported = ref(false)
  const qrcodeReaderActive = ref(false)
  const captureRef = ref<InstanceType<typeof QrcodeCapture> | null>(null)

  function openGallery () {
    captureRef.value?.$el.click()
  }

  function onCameraOn (capabilities: MediaTrackCapabilities & { torch?: boolean }) {
    // console.log(capabilities)
    torchNotSupported.value = !capabilities.torch
  }

  const onDetect = (detectedCodes: DetectedBarcode[]) => {
    // https://verified.eurogold.co.id/SN-0001
    const fullUrl = detectedCodes[0]!.rawValue
    const url = new URL(fullUrl)

    if (!fullUrl.includes('eurogold')) {
      return toastAdd('QR Code tidak sesuai')
    }

    // remove the url and just get the invitation product unit code
    // we don't redirect to the full url because we don't want to support
    // any QR Code that may open to unrelated websites.
    const code = url.pathname.substring(1)
    const signature = url.searchParams.get('signature')

    router.replace({ name: 'product unit show', params: { code: code }, query: { signature } })
  }

  // toast
  const { add: toastAdd } = useToast()
</script>

<template>
  <div>
    <div
      v-if="qrcodeReaderActive"
      class="relative">
      <div class="h-screen">
        <qrcode-stream
          :torch="torchActive"
          @camera-on="onCameraOn"
          @detect="onDetect" />

        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div class="relative size-64">
            <span class="absolute top-0 left-0 size-8 border-t-4 border-l-4 border-blue-900 rounded-tl-md"></span>
            <span class="absolute top-0 right-0 size-8 border-t-4 border-r-4 border-blue-900 rounded-tr-md"></span>
            <span class="absolute bottom-0 left-0 size-8 border-b-4 border-l-4 border-blue-900 rounded-bl-md"></span>
            <span class="absolute bottom-0 right-0 size-8 border-b-4 border-r-4 border-blue-900 rounded-br-md"></span>

            <!-- <span class="absolute inset-x-2 top-0 h-0.5 bg-danger animate-scan"></span> -->
          </div>
        </div>

        <qrcode-capture
          ref="captureRef"
          class="hidden"
          :capture="undefined"
          @detect="onDetect"
          @error="() => toastAdd('QR Code tidak valid')" />
      </div>
      <div class="absolute top-0 p-4 inset-x-0">
        <div class="flex items-center gap-4">
          <app-button
            title="Kembali"
            class="mr-auto bg-black/40 text-white rounded-full text-lg size-10 flex items-center justify-center"
            @click="qrcodeReaderActive = false">
            <fa-icon icon="i-far-angle-left" />
          </app-button>
          <app-button
            v-if="!torchNotSupported"
            title="Blitz"
            class="bg-black/40 text-white rounded-full text-lg size-10 flex items-center justify-center"
            @click="torchActive = !torchActive">
            <fa-icon
              v-if="torchActive"
              icon="i-far-bolt-lightning" />
            <fa-icon
              v-else
              icon="i-far-bolt-slash" />
          </app-button>
          <app-button
            title="Ambil dari Galeri"
            class="bg-black/40 text-white rounded-full text-lg size-10 flex items-center justify-center"
            @click="openGallery">
            <fa-icon icon="i-far-images" />
          </app-button>
        </div>
        <div class="mt-4 font-sans">
          <h1 class="text-2xl md:text-3xl font-bold">
            Scan QR Code
          </h1>
          <p class="text-sm md:text-base">
            Point your camera at a QR Code to scan.
          </p>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="text-center flex flex-col items-center justify-between py-20 min-h-screen">
        <div class="flex flex-col items-center gap-10">
          <img
            src="@/assets/images/logo-app.webp"
            alt="Eurogold"
            class="size-16">
          <img
            src="@/assets/images/eurogold.webp"
            alt="Eurogold"
            class="w-[16rem]">
        </div>
        <div class="space-y-2 font-sans tracking-wider">
          <h1 class="text-2xl md:text-3xl uppercase font-bold">
            Scan & Sure
          </h1>
          <p class="text-sm md:text-base">
            Scan Your Gold Here to Check Its Authenticity
          </p>
          <app-button
            class="text-xl bg-blue-900 py-2 px-8 rounded-full mt-3"
            @click="qrcodeReaderActive = true">
            <div class="flex items-center gap-2">
              <img
                src="@/assets/images/scan-icon.svg"
                alt="Eurogold">
              <span class="font-semibold tracking-wide">Scan Here</span>
            </div>
          </app-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes scan {
  0%,
  100% { top: 0%; }
  50% { top: 100%; }
}

.animate-scan {
  animation: scan 4s ease-in-out infinite;
}
</style>
